import { getDateForTable } from '../../agent-production/components/agentProfile/tabs/helpers/exportHelpers';
import { agentsColumns, tableViewNames } from '../../agent-production/constants/agentProductionConstants';
import {
    boolToShortString,
    centupleDecimalFormatter, formatClr, formatDecimal,
    formatPercent,
    getDecimalNumber,
    getIntegerFormat, getListingStatus,
    getUngroupedNoDecimals, hasNoLength, shortDateFormatWithSlashes
} from '../../agent-production/helpers/dataFormatters';
import { searchTerms as st } from '../../constants';
import { getSum } from '../../market-share/helpers/helpers';

export const handleSortedHighlight = async (e) => {
    const columns = e.api.getColumns();
    const sortedColumn = columns?.find((column) => column.isSorting());
    const columnIndex = (e.api.getColumns()?.indexOf(sortedColumn)) + 1;
    const CLASS_HIGHLIGHT = 'highlighted-column';
    const ATTR_COLUMN_INDEX = 'aria-colindex';

    document.querySelectorAll('.ag-header-cell').forEach((columnHeaderCell) => {
        columnHeaderCell.classList.remove(CLASS_HIGHLIGHT);
    });
    document.querySelectorAll('.ag-floating-filter.ag-header-cell').forEach((columnHeaderCell) => {
        columnHeaderCell.classList.remove(CLASS_HIGHLIGHT);
    });

    const sortedColumnHeaderCell = document.querySelector(
        `.ag-header-cell[${ATTR_COLUMN_INDEX}="${columnIndex}"]`
    );
    const sortedFloatingFilterCell = document.querySelector(
        `.ag-floating-filter.ag-header-cell[${ATTR_COLUMN_INDEX}="${columnIndex}"]`
    );

    if (sortedColumnHeaderCell) sortedColumnHeaderCell.classList.add(CLASS_HIGHLIGHT);
    if (sortedFloatingFilterCell) sortedFloatingFilterCell.classList.add(CLASS_HIGHLIGHT);
    e.api.refreshCells({ force: true });
};

export const maskInvalidValues = ({ value, defaultValue, getFormattedValue }) =>
    hasNoLength(value)
        ? defaultValue
        : getFormattedValue(value);

export const csvFormats = {
    integer: {
        format: value => maskInvalidValues({
            value,
            defaultValue: '0',
            getFormattedValue: getIntegerFormat
        })
    },
    volume: {
        format: value => getUngroupedNoDecimals(value)
    },
    date: {
        format: value => shortDateFormatWithSlashes(value)
    },
    oneDecimal: {
        format: value => formatDecimal(value, 1)
    },
    booleanShort: {
        format: value => boolToShortString(value)
    },
    listingStatus: {
        format: value => getListingStatus(value)
    },
    decimal: {
        format: value => maskInvalidValues({
            value,
            defaultValue: '0',
            getFormattedValue: getDecimalNumber
        })
    },
    percentage: {
        format: value => formatPercent(value, 1)
    },
    percent2Decimals: {
        format: value => formatPercent(value, 2)
    },
    centuplePercentage: {
        format: value => formatPercent(((value || 0) * 100), 2)
    },
    average: {
        format: value => centupleDecimalFormatter(value)
    },
    percentageEmptyCell: {
        format: value => formatClr(value)
    }
};

export const filenameTransformer = {
    input: function(value) {
        this.value = value;
        return this;
    },
    replaceDots: function() {
        // AG Grid will not add a filename suffix (eg. '.csv') if the filename contains a dot, so remove all dots
        this.value = this.value.replaceAll('.', '');
        return this;
    },
    fixApostrophes: function() {
        // REMAX's's becomes REMAX's
        this.value = this.value.replaceAll('\'s\'s', '\'s');
        // Properties's becomes Properties'
        this.value = this.value.replaceAll('s\'s', 's\'');
        // REALTORS's becomes REALTORS'
        this.value = this.value.replaceAll('S\'s', 'S\'');
        return this;
    },
    removeRepeats: function() {
        this.value = this.value.replaceAll('Listings Listings', 'Listings');
        return this;
    },
    output: function() {
        return this.value;
    }
};

export const getScrubbedFilenameForExport = filename => {
    return filenameTransformer.input(filename)
        .replaceDots()
        .fixApostrophes()
        .removeRepeats()
        .output();
};

export const shouldExportColumn = column => column?.visible ?? column?.shouldExport ?? !column?.hide;

export const getExportCellValue = cell => {
    if (isEncodedCellValue(cell?.value)) return getDecodedCellValue(cell?.value);
    if (cell?.column?.colDef?.exportFormatter) return cell?.column?.colDef?.exportFormatter(cell?.value);
    if (cell?.column?.colDef?.valueFormatter) return cell?.column?.colDef?.valueFormatter(cell);
    if (cell?.column?.colId === agentsColumns.officeName.id && cell?.value === undefined) return st.totals;
    if (cell?.column?.colId === agentsColumns.agProdMonth.id) return getDateForTable(new Date(cell.value))
    return cell?.value;
};

export const getExportHeaderText = header => {
    const exportHeaderLabel = header?.column?.colDef?.exportHeaderLabel ??
        header?.column?.colDef?.headerComponentParams?.titleWithTooltip?.props?.headerDetails?.label;
    return exportHeaderLabel !== undefined ? exportHeaderLabel : header?.column?.colDef?.headerName;
};

export const getCalculatedPct = ({ colDef, pinnedRowData, keyField }) => {
    return pinnedRowData?.[keyField || colDef.field] * 100 ;
};

export const getPinnedCellValue = ({ pinnedRowData, keyField }) => {
    return pinnedRowData?.[keyField];
};

export const getColumnSum = ({ rowData, colDef }) => {
    return getSum(rowData, colDef.field);
};

export const FLAG_USE_CELL_VALUE = '*****-USE-CELL-VALUE-*****';
export const getEncodedCellValue = value => value + FLAG_USE_CELL_VALUE;
export const getDecodedCellValue = value => value?.replace(FLAG_USE_CELL_VALUE, '');
export const isEncodedCellValue = value => value?.toString().indexOf(FLAG_USE_CELL_VALUE) >= 0;

export const removeRowByKey = (gridRef, key, value) => {
    gridRef.current.api.forEachNode(node => {
        if (node.data[key] === value) {
            gridRef.current.api.applyTransaction({ remove: [node.data] });
        }
    });
};

export const getRowIdParam = (tableViewId) => {
    const isRankedTable = [
        tableViewNames.overview,
        tableViewNames.contactInformation,
        tableViewNames.growthAnalysisLtm,
        tableViewNames.growthAnalysisYtd,
        tableViewNames.listingProficiency
    ].includes(tableViewId);
    return isRankedTable ? {getRowId: (params) => `${params.context.tableViewId}-agent-${params.data?.agentId}`} : {};
 };

export const getRankedData = (data, keyFieldToRank) => {
    if (!keyFieldToRank) return data.map((obj, i) => ({ ...obj, rank: i + 1 }));
    let rank = 1;
    let prevValue = data[0]?.[keyFieldToRank];
    return data.map((obj, i) => {
        const currentValue = obj?.[keyFieldToRank];
        if (i === 0 || currentValue === prevValue) return { ...obj, rank };
        if (currentValue !== prevValue) rank = i + 1;
        prevValue = currentValue;
        return { ...obj, rank };
    });
};

export const setAgGridSort = ({api, sortState}) => {
    if (!api || !sortState) return;
    const currentColumnState = api.getColumnState();
    const isGridSortApplied = currentColumnState.some(col =>
        col.colId === sortState.colId && col.sort === sortState.sort
    );
    if (!isGridSortApplied) {
        api.applyColumnState({
            state: [{ colId: sortState.colId, sort: sortState.sort }],
            defaultState: { sort: null }
        });
    }
};

export const setAgGridFloatingFilter = ({api, filterState}) => {
    if (!api || !filterState) return;
    const filterModel = {};
    Object.entries(filterState).forEach(([colName, value]) => {
        filterModel[colName] = { type: 'contains', filter: value };
    });
    api.setFilterModel(filterModel);
};

export const applyPersistedSelection = (api, selectedAgentIds, enabled) => {
    if (!enabled || !api) return;
    const selectedIds = new Set(selectedAgentIds || []);
    const toSelect = [], toDeselect = [];
    api.forEachNode(node => {
        const shouldBeSelected = selectedIds.has(node.data?.agentId);
        if (node.isSelected() !== shouldBeSelected) (shouldBeSelected ? toSelect : toDeselect).push(node);
    });
    if (toSelect.length) api.setNodesSelected({ nodes: toSelect, newValue: true, source: 'api' });
    if (toDeselect.length) api.setNodesSelected({ nodes: toDeselect, newValue: false, source: 'api' });
};
